import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useMissingOriginalAmounts } from './useMissingOriginalAmounts';
import { formatCentsAud } from './radarFormat';
import {
  buildUploadPreview,
  parseDollarsToCents,
  REJECTION_TEXT,
  REQUIRED_COLUMNS,
  type UploadPreview,
} from './originalAmountInput';
import type { MissingOriginalRow, OriginalAmountEntry, OriginalAmountResult } from './types';

/**
 * "Complete original amounts": the loans on the radar whose lender never
 * reported an original amount. The tenant types an amount per loan, or uploads
 * their own CSV INTO VALUTTEN, so paid-down can be exact. Nothing is exported:
 * no download, no template file; the required columns are stated as text.
 */

// The radar's single money formatter, so a balance here matches the radar to the dollar.
const formatCents = formatCentsAud;

interface Props {
  companyId: string | undefined;
  /** Called after any successful save so the radar can refresh its paid-down figures. */
  onSaved: () => void;
}

const MissingOriginalAmountsPanel: React.FC<Props> = ({ companyId, onSaved }) => {
  const [page, setPage] = useState(1);
  const { data, loading, error, reload, save } = useMissingOriginalAmounts(companyId, page);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rowStatus, setRowStatus] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<UploadPreview | null>(null);
  const [uploadResults, setUploadResults] = useState<OriginalAmountResult[] | null>(null);

  const applyResults = (results: OriginalAmountResult[]) => {
    const next: Record<string, string> = {};
    for (const r of results) next[r.loanId] = r.ok ? 'Saved' : REJECTION_TEXT[r.reason] ?? 'Not saved';
    setRowStatus((s) => ({ ...s, ...next }));
    if (results.some((r) => r.ok)) {
      setDrafts((d) => {
        const copy = { ...d };
        for (const r of results) if (r.ok) delete copy[r.loanId];
        return copy;
      });
      onSaved();
      void reload();
    }
  };

  const submit = async (entries: OriginalAmountEntry[], source: 'manual' | 'upload') => {
    setSaving(true);
    try {
      const res = await save(entries, source);
      if (source === 'upload') setUploadResults(res.results);
      applyResults(res.results);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setRowStatus((s) => ({ ...s, ...Object.fromEntries(entries.map((en) => [en.loanId, msg])) }));
    } finally {
      setSaving(false);
    }
  };

  const draftEntries = (loanIds: string[]): OriginalAmountEntry[] =>
    loanIds.flatMap((id) => {
      const parsed = parseDollarsToCents(drafts[id] ?? '');
      return parsed.ok ? [{ loanId: id, originalAmountCents: parsed.cents }] : [];
    });

  const editedValid = Object.keys(drafts).filter((id) => parseDollarsToCents(drafts[id]).ok);

  const onFile = (file: File | undefined) => {
    setUploadResults(null);
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setPreview({ ok: false, error: 'Please choose a .csv file. Save a spreadsheet as CSV first.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(buildUploadPreview(String(reader.result ?? '')));
    reader.onerror = () => setPreview({ ok: false, error: 'The file could not be read.' });
    reader.readAsText(file);
  };

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-4" aria-busy="true">
        <SkeletonTable rows={6} columns={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
        {error}
      </div>
    );
  }
  if (!data || !data.available) return null;

  const validPreviewRows = preview?.ok ? preview.rows.filter((r) => r.cents !== null) : [];
  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <section className="space-y-4 rounded-xl border border-gray-700 bg-gray-800/30 p-4" aria-label="Complete original amounts">
      <div>
        <h3 className="text-sm font-semibold text-white">Complete original amounts</h3>
        <p className="mt-1 text-xs text-gray-400">
          Your lenders did not report an original amount for these {data.total} active loans, so paid down is measured
          from the first statement. Enter the amount from the loan contract to make it exact. It can&apos;t be lower
          than the first balance your lender reported.
        </p>
      </div>

      <div className="rounded-lg border border-gray-700 p-3 text-xs text-gray-300">
        <label className="flex flex-wrap items-center gap-2">
          <Upload className="h-4 w-4 text-blue-400" />
          <span>Upload a CSV with the columns</span>
          <code className="rounded bg-gray-900 px-1">{REQUIRED_COLUMNS[0]}</code>
          <span>and</span>
          <code className="rounded bg-gray-900 px-1">{REQUIRED_COLUMNS[1]}</code>
          <span>(dollars, e.g. 612500 or 612,500.50):</span>
          <input
            type="file"
            accept=".csv"
            aria-label="Upload original amounts CSV"
            onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs text-gray-300"
          />
        </label>
        {preview && !preview.ok && (
          <p className="mt-2 text-red-300" role="alert">
            {preview.error}
          </p>
        )}
        {preview?.ok && (
          <div className="mt-3 space-y-2">
            <p>
              {validPreviewRows.length} of {preview.rows.length} rows are ready to save.
            </p>
            <div className="max-h-64 overflow-auto rounded border border-gray-700">
              <table className="min-w-full text-xs" data-testid="upload-preview">
                <thead className="bg-gray-800/60 text-left text-gray-400">
                  <tr>
                    <th className="px-2 py-1">Row</th>
                    <th className="px-2 py-1">Loan</th>
                    <th className="px-2 py-1">Amount</th>
                    <th className="px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const server = uploadResults?.find((x) => x.loanId === r.loanId);
                    const status = r.error
                      ? r.error
                      : server
                        ? server.ok
                          ? 'Saved'
                          : REJECTION_TEXT[server.reason] ?? 'Not saved'
                        : 'Ready';
                    return (
                      <tr key={`${r.line}-${r.loanId}`} className={r.error || (server && !server.ok) ? 'text-red-300' : ''}>
                        <td className="px-2 py-1">{r.line}</td>
                        <td className="px-2 py-1">{r.loanId}</td>
                        <td className="px-2 py-1">{r.cents !== null ? formatCents(r.cents) : r.rawAmount}</td>
                        <td className="px-2 py-1">{status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              disabled={saving || validPreviewRows.length === 0}
              onClick={() =>
                void submit(
                  validPreviewRows.map((r) => ({ loanId: r.loanId, originalAmountCents: r.cents as number })),
                  'upload',
                )
              }
              className="rounded-md border border-blue-500/40 px-3 py-1.5 font-medium text-blue-300 hover:bg-blue-500/15 disabled:opacity-50"
            >
              Save {validPreviewRows.length} uploaded amounts
            </button>
          </div>
        )}
      </div>

      {data.rows.length === 0 ? (
        <p className="text-sm text-gray-300" role="status">
          Every active loan has an original amount.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700 text-sm">
              <thead className="bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Loan</th>
                  <th className="px-3 py-2">Lender</th>
                  <th className="px-3 py-2">Settled</th>
                  <th className="px-3 py-2 text-right">First balance</th>
                  <th className="px-3 py-2 text-right">Latest balance</th>
                  <th className="px-3 py-2">Original amount</th>
                  <th className="px-3 py-2">
                    <span className="sr-only">Save</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {data.rows.map((row: MissingOriginalRow) => {
                  const draft = drafts[row.loanId] ?? '';
                  const parsed = draft ? parseDollarsToCents(draft) : null;
                  return (
                    <tr key={row.loanId} data-testid="missing-row">
                      <td className="px-3 py-2 text-white">{row.clientDisplay ?? 'n/a'}</td>
                      <td className="px-3 py-2 text-gray-300">{row.loanId}</td>
                      <td className="px-3 py-2 text-gray-300">{row.lender ?? 'n/a'}</td>
                      <td className="px-3 py-2 text-gray-300">{row.settlementDate ?? 'n/a'}</td>
                      <td className="px-3 py-2 text-right text-gray-300">
                        {formatCents(row.firstBalanceCents)}
                        {row.firstStatementMonth ? <span className="block text-xs text-gray-500">{row.firstStatementMonth}</span> : null}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-300">{formatCents(row.latestBalanceCents)}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Original amount for loan ${row.loanId}`}
                          value={draft}
                          placeholder="$"
                          onChange={(e) => setDrafts((d) => ({ ...d, [row.loanId]: e.target.value }))}
                          className="w-32 rounded-md border border-gray-600 bg-gray-900 px-2 py-1 text-sm text-white"
                        />
                        {parsed && !parsed.ok && <span className="block text-xs text-red-300">{parsed.error}</span>}
                        {rowStatus[row.loanId] && <span className="block text-xs text-gray-400">{rowStatus[row.loanId]}</span>}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          disabled={saving || !parsed?.ok}
                          onClick={() => void submit(draftEntries([row.loanId]), 'manual')}
                          aria-label={`Save original amount for loan ${row.loanId}`}
                          className="rounded-md border border-blue-500/40 px-2 py-1 text-xs font-medium text-blue-300 hover:bg-blue-500/15 disabled:opacity-50"
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
            <button
              type="button"
              disabled={saving || editedValid.length === 0}
              onClick={() => void submit(draftEntries(editedValid), 'manual')}
              className="rounded-md border border-blue-500/40 px-3 py-1.5 font-medium text-blue-300 hover:bg-blue-500/15 disabled:opacity-50"
            >
              Save all edited ({editedValid.length})
            </button>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="underline disabled:opacity-40">
                  Previous
                </button>
                <span>
                  Page {page} of {pages}
                </span>
                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="underline disabled:opacity-40">
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default MissingOriginalAmountsPanel;
